import React, { useState, useCallback } from 'react';
import { Modal, Slider, Button, Space, Typography, Row, Col, Card } from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import { Area, Point } from 'react-easy-crop/types';

const { Text, Title } = Typography;

interface ImageCropModalProps {
  visible: boolean;
  imageUrl: string;
  onSave: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
  onCancel: () => void;
  aspectRatio?: number; // e.g., 16/9, 4/3, 1 (square), or undefined for free
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  visible,
  imageUrl,
  onSave,
  onCancel,
  aspectRatio = 4 / 3, // Default to 4:3 aspect ratio
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    rotation = 0
  ): Promise<{ blob: Blob; url: string }> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          const fileUrl = URL.createObjectURL(blob);
          resolve({ blob, url: fileUrl });
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) return;

    setSaving(true);
    try {
      const { blob, url } = await getCroppedImg(imageUrl, croppedAreaPixels, rotation);
      onSave(blob, url);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleClose = () => {
    // Reset state
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    onCancel();
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          Edit & Crop Image
        </Title>
      }
      open={visible}
      onCancel={handleClose}
      width={900}
      centered
      footer={[
        <Button key="cancel" onClick={handleClose} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          icon={<SaveOutlined />}
        >
          Save & Upload
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Crop Area */}
        <Card>
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 400,
              backgroundColor: '#333',
            }}
          >
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onRotationChange={setRotation}
            />
          </div>
        </Card>

        {/* Controls */}
        <Card title="Adjust Image" size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Zoom Control */}
            <div>
              <Row align="middle" gutter={16}>
                <Col flex="80px">
                  <Text strong>
                    <ZoomOutOutlined /> Zoom
                  </Text>
                </Col>
                <Col flex="auto">
                  <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={setZoom}
                    tooltip={{ formatter: (value) => `${Math.round((value || 1) * 100)}%` }}
                  />
                </Col>
                <Col flex="60px">
                  <Text type="secondary">{Math.round(zoom * 100)}%</Text>
                </Col>
              </Row>
            </div>

            {/* Rotation Control */}
            <div>
              <Row align="middle" gutter={16}>
                <Col flex="80px">
                  <Text strong>Rotate</Text>
                </Col>
                <Col flex="auto">
                  <Slider
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    onChange={setRotation}
                    tooltip={{ formatter: (value) => `${value}°` }}
                  />
                </Col>
                <Col flex="60px">
                  <Text type="secondary">{rotation}°</Text>
                </Col>
              </Row>
            </div>

            {/* Quick Rotate Buttons */}
            <div>
              <Space>
                <Button
                  icon={<RotateLeftOutlined />}
                  onClick={handleRotateLeft}
                >
                  Rotate Left
                </Button>
                <Button
                  icon={<RotateRightOutlined />}
                  onClick={handleRotateRight}
                >
                  Rotate Right
                </Button>
                <Button
                  onClick={() => {
                    setZoom(1);
                    setRotation(0);
                    setCrop({ x: 0, y: 0 });
                  }}
                >
                  Reset
                </Button>
              </Space>
            </div>
          </Space>
        </Card>

        {/* Instructions */}
        <Card size="small" style={{ backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}>
          <Space direction="vertical" size="small">
            <Text strong>💡 Tips:</Text>
            <Text type="secondary">• Drag the image to reposition</Text>
            <Text type="secondary">• Use the zoom slider or scroll wheel to zoom in/out</Text>
            <Text type="secondary">• Drag the corners to adjust the crop area</Text>
            <Text type="secondary">• Rotate the image if needed</Text>
          </Space>
        </Card>
      </Space>
    </Modal>
  );
};

export default ImageCropModal;
